interface ReadinessRingProps {
  /** 0–100. `null` is the cold-start case: a dashed ring and a "?" . */
  percentage: number | null;
  band?: string | undefined;
  size?: number | undefined;
}

/**
 * The Readiness Ring.
 *
 * `r=23 → C=144.51` comes straight from the brand kit; the dash array is
 * `pct × C` so the arc is the number rather than an approximation of it.
 *
 * A null score draws a dashed ring instead of a 0% one. Showing an empty ring
 * to a student on day one reads as "you know nothing", which is both untrue
 * and the opposite of the product's job.
 *
 * At small sizes (≤ 80px, used in subject cards) the number scales down and
 * the band label is hidden — it's already shown in the card text, and cramming
 * two lines into 64px clips the type.
 */
const RADIUS = 23;
const CIRCUMFERENCE = 144.51;

export function ReadinessRing({ percentage, band, size = 132 }: ReadinessRingProps) {
  const isColdStart = percentage === null;
  const filled = isColdStart ? 0 : (Math.min(100, Math.max(0, percentage)) / 100) * CIRCUMFERENCE;

  const small = size <= 80;

  return (
    <div
      className="relative grid shrink-0 place-items-center"
      style={{ width: size, height: size }}
      role="img"
      aria-label={isColdStart ? "Readiness not measured yet" : `Readiness ${percentage}%, ${band}`}
    >
      <svg viewBox="0 0 56 56" className="absolute inset-0 h-full w-full -rotate-90">
        <circle
          cx="28"
          cy="28"
          r={RADIUS}
          fill="none"
          stroke="var(--border-strong)"
          strokeWidth="4"
          strokeDasharray={isColdStart ? "4 5" : undefined}
        />
        {!isColdStart && (
          <circle
            cx="28"
            cy="28"
            r={RADIUS}
            fill="none"
            stroke="var(--accent)"
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={`${filled} ${CIRCUMFERENCE}`}
          />
        )}
      </svg>

      <div className="flex flex-col items-center text-center">
        {small ? (
          /* Compact: number + % on one line, no band */
          <span className="tabular font-[family-name:var(--font-display)] text-[16px] font-bold leading-none">
            {isColdStart ? "?" : percentage}
            {!isColdStart && <span className="text-[10px]">%</span>}
          </span>
        ) : (
          /* Full: large number, band label below */
          <>
            <span className="tabular font-[family-name:var(--font-display)] text-[34px] font-bold leading-none">
              {isColdStart ? "?" : percentage}
              {!isColdStart && <span className="text-[18px]">%</span>}
            </span>
            {band ? <span className="pt-1 text-[11px] text-text-muted">{band}</span> : null}
          </>
        )}
      </div>
    </div>
  );
}
